/**
 * Dynamic model discovery via GitLab GraphQL API.
 *
 * Queries `aiChatAvailableModels` to discover which models are available
 * for a given GitLab namespace. Used to dynamically populate `duo-workflow-*`
 * model IDs at runtime.
 *
 * Requires GitLab 18.4+ (18.5+ for pinned model support).
 */

import { GitLabError } from './gitlab-error';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AiModel {
  /** Display name (e.g., 'Claude Sonnet 4.6') */
  name: string;
  /** Model reference for API use (e.g., 'claude_sonnet_4_6' or 'anthropic/claude-sonnet-4-5-20250929') */
  ref: string;
}

export interface AiChatAvailableModels {
  defaultModel: AiModel | null;
  selectableModels: AiModel[];
  pinnedModel: AiModel | null;
}

export interface DiscoveredModels {
  /** The effective model (pinned > user-selected > default) */
  defaultModel: AiModel | null;
  /** All models the user can select from */
  selectableModels: AiModel[];
  /** Admin-pinned model (overrides everything) */
  pinnedModel: AiModel | null;
  /** Whether the ai_user_model_switching feature flag is enabled */
  modelSwitchingEnabled: boolean;
  /** GitLab instance version */
  instanceVersion: string | null;
}

export interface ModelDiscoveryConfig {
  /** GitLab instance URL */
  instanceUrl: string;
  /** Function returning auth headers */
  getHeaders: () => Record<string, string>;
  /** Custom fetch */
  fetch?: typeof fetch;
}

// ---------------------------------------------------------------------------
// GraphQL query (inlined — no need for graphql-request gql tag)
// ---------------------------------------------------------------------------

const AI_CHAT_AVAILABLE_MODELS_QUERY = `
  query aiChatAvailableModels($rootNamespaceId: GroupID!) {
    metadata {
      featureFlags(names: ["ai_user_model_switching"]) {
        enabled
        name
      }
      version
    }

    aiChatAvailableModels(rootNamespaceId: $rootNamespaceId) {
      defaultModel {
        name
        ref
      }
      selectableModels {
        name
        ref
      }
      pinnedModel {
        name
        ref
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

const DISCOVERY_CACHE_TTL_MS = 10 * 60 * 1000;

interface CachedDiscovery {
  data: DiscoveredModels;
  expiresAt: number;
}

export class GitLabModelDiscovery {
  private readonly config: ModelDiscoveryConfig;
  private readonly fetchFn: typeof fetch;
  private cache = new Map<string, CachedDiscovery>();

  constructor(config: ModelDiscoveryConfig) {
    this.config = config;
    this.fetchFn = config.fetch ?? fetch;
  }

  /**
   * Discover available models for a given root namespace.
   *
   * Results are cached per `rootNamespaceId` with a 10-minute TTL.
   * Use `invalidateCache()` to force an immediate refresh.
   *
   * @param rootNamespaceId - GitLab group ID (e.g., 'gid://gitlab/Group/12345')
   */
  async discover(rootNamespaceId: string): Promise<DiscoveredModels> {
    const cached = this.cache.get(rootNamespaceId);
    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }

    const url = `${this.config.instanceUrl}/api/graphql`;

    try {
      const response = await this.fetchFn(url, {
        method: 'POST',
        headers: {
          ...this.config.getHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: AI_CHAT_AVAILABLE_MODELS_QUERY,
          variables: { rootNamespaceId },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new GitLabError({
          message: `Model discovery GraphQL request failed: ${response.status} ${response.statusText} - ${errorText}`,
          statusCode: response.status,
          responseBody: errorText,
        });
      }

      const json = (await response.json()) as {
        data?: {
          aiChatAvailableModels?: AiChatAvailableModels | null;
          metadata?: {
            featureFlags?: Array<{ name: string; enabled: boolean }>;
            version?: string;
          } | null;
        };
        errors?: Array<{ message: string }>;
      };

      if (json.errors && json.errors.length > 0) {
        throw new GitLabError({
          message: `Model discovery GraphQL errors: ${json.errors.map((e) => e.message).join(', ')}`,
        });
      }

      const models = json.data?.aiChatAvailableModels;
      const metadata = json.data?.metadata;

      const modelSwitchingEnabled =
        metadata?.featureFlags?.find((f) => f.name === 'ai_user_model_switching')?.enabled ?? false;

      const result: DiscoveredModels = {
        defaultModel: models?.defaultModel ?? null,
        selectableModels: models?.selectableModels ?? [],
        pinnedModel: models?.pinnedModel ?? null,
        modelSwitchingEnabled,
        instanceVersion: metadata?.version ?? null,
      };

      this.cache.set(rootNamespaceId, {
        data: result,
        expiresAt: Date.now() + DISCOVERY_CACHE_TTL_MS,
      });
      return result;
    } catch (error) {
      if (error instanceof GitLabError) throw error;
      throw new GitLabError({
        message: `Model discovery failed: ${error}`,
        cause: error,
      });
    }
  }

  /**
   * Get the effective model ref to use for a workflow.
   *
   * Priority: pinned > user-selected > default.
   *
   * @param rootNamespaceId - GitLab group ID
   * @param userSelectedRef - Optional user preference
   */
  async getEffectiveModelRef(
    rootNamespaceId: string,
    userSelectedRef?: string
  ): Promise<string | null> {
    const discovered = await this.discover(rootNamespaceId);

    // Pinned model always wins
    if (discovered.pinnedModel) {
      return discovered.pinnedModel.ref;
    }

    // User selection (if model switching is enabled)
    if (userSelectedRef && discovered.modelSwitchingEnabled) {
      const isValid = discovered.selectableModels.some((m) => m.ref === userSelectedRef);
      if (isValid) {
        return userSelectedRef;
      }
    }

    // Default
    return discovered.defaultModel?.ref ?? null;
  }

  /**
   * Invalidate the cached discovery results.
   */
  invalidateCache(): void {
    this.cache.clear();
  }
}
